import * as React from 'react';
import { AviaTraveller, TravellerFieldEnum, useAddTravellersDataMutation } from '@websky/graphql';
import { useDispatch } from 'react-redux';
import { fillOrder } from '../Checkout/store/order/actions';
import { ApolloError } from 'apollo-boost';

export interface ChangeLinkReturn {
	isRelinkingMode: boolean;
	relinkingPassenger: AviaTraveller;
	relinkingToPassengerId: string;
	isLoading: boolean;
	error: string;
	clearError: () => void;
	onSetRelinkingMode: (traveller: AviaTraveller) => void;
	onCloseRelinkMode: () => void;
	onLinkTo: (passengerId: string) => void;
	confirmRelinking: () => Promise<void>;
}

export const useChangeLink = (orderId: string): ChangeLinkReturn => {
	const [addTravellersDataMutation, { loading }] = useAddTravellersDataMutation();
	const dispatch = useDispatch();

	const [relinkingPassenger, setRelinkingPassenger] = React.useState<AviaTraveller>(null);
	const [relinkingToPassengerId, setRelinkingToPassengerId] = React.useState<string>(null);
	const [error, setError] = React.useState<string>(null);

	const onSetRelinkingMode = (traveller: AviaTraveller) => {
		setRelinkingPassenger(traveller);
		setRelinkingToPassengerId(traveller.linkedTraveller.id);
	};

	const onCloseRelinkMode = () => {
		setRelinkingPassenger(null);
		setRelinkingToPassengerId(null);
	};

	const onLinkTo = (passengerId: string) => {
		setRelinkingToPassengerId(passengerId);
	};

	const confirmRelinking = async () => {
		try {
			const { data } = await addTravellersDataMutation({
				variables: {
					variables: {
						orderId,
						travellersData: [
							{
								id: relinkingPassenger.id,
								values: [
									{
										type: TravellerFieldEnum.LinkedTraveller,
										value: relinkingToPassengerId
									}
								]
							}
						]
					}
				}
			});

			if (data.SetTravellersData) {
				dispatch(fillOrder(data.SetTravellersData));

				setRelinkingPassenger(null);
				setRelinkingToPassengerId(null);
			}
		} catch (e) {
			setError((e as ApolloError)?.graphQLErrors?.[0]?.message || 'Error during relinking');
		}
	};

	const clearError = () => setError(null);

	return {
		isRelinkingMode: !!relinkingPassenger,
		relinkingPassenger,
		relinkingToPassengerId,
		isLoading: loading,
		error,
		clearError,
		onSetRelinkingMode,
		onCloseRelinkMode,
		onLinkTo,
		confirmRelinking
	};
};
